// DESCRIPTION: React component for the Google Calendar integration settings panel.
// DESCRIPTION: Handles connect/disconnect OAuth flow and Calendar ID configuration.

import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { Button, Notice, Spinner, TextControl } from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';

/**
 * GoogleCalendarSettings Component
 *
 * Renders the Google Calendar integration panel inside the Settings → Integrations tab.
 * Allows the site owner to enter their Google OAuth credentials, connect their Google
 * account via OAuth, and set the target Calendar ID.
 *
 * @param {Object}   props
 * @param {Object}   props.settings      Current plugin settings object.
 * @param {Function} props.updateSetting Callback to update a single setting key.
 * @param {Function} props.addNotice     Callback to surface a notice in the parent.
 */
export default function GoogleCalendarSettings( {
	settings,
	updateSetting,
	addNotice,
} ) {
	const [ status, setStatus ] = useState( null );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ isConnecting, setIsConnecting ] = useState( false );
	const [ isDisconnecting, setIsDisconnecting ] = useState( false );
	const [ isSavingCalId, setIsSavingCalId ] = useState( false );
	const [ calendarId, setCalendarId ] = useState( 'primary' );

	// Check for OAuth result query params on mount (after redirect-back from Google).
	useEffect( () => {
		const params = new URLSearchParams( window.location.search );
		if ( params.get( 'google_auth_success' ) === '1' ) {
			addNotice(
				__(
					'Google Calendar connected successfully.',
					'nemtly-booking'
				),
				'success'
			);
			// Clean the URL so a page refresh doesn't re-show the notice.
			const clean = new URL( window.location.href );
			clean.searchParams.delete( 'google_auth_success' );
			window.history.replaceState( {}, '', clean.toString() );
		}
		if ( params.get( 'google_auth_error' ) === '1' ) {
			addNotice(
				__(
					'Google Calendar connection failed. Please try again.',
					'nemtly-booking'
				),
				'error'
			);
			const clean = new URL( window.location.href );
			clean.searchParams.delete( 'google_auth_error' );
			window.history.replaceState( {}, '', clean.toString() );
		}

		loadStatus();
	}, [] );

	const loadStatus = async () => {
		setIsLoading( true );
		try {
			const response = await apiFetch( {
				path: '/nemtly-booking/v1/google-auth/status',
			} );
			if ( response && response.success ) {
				setStatus( response.data );
				setCalendarId( response.data.calendar_id || 'primary' );
			}
		} catch ( error ) {
			addNotice(
				__(
					'Failed to load Google Calendar status.',
					'nemtly-booking'
				),
				'error'
			);
		} finally {
			setIsLoading( false );
		}
	};

	const handleConnect = async () => {
		setIsConnecting( true );
		try {
			const response = await apiFetch( {
				path: '/nemtly-booking/v1/google-auth/connect',
			} );
			if ( response && response.success && response.data.auth_url ) {
				// Redirect the browser to Google's OAuth consent screen.
				window.location.href = response.data.auth_url;
			}
		} catch ( error ) {
			const message =
				error?.message ||
				__(
					'Failed to initiate Google connection. Make sure your Client ID and Secret are saved first.',
					'nemtly-booking'
				);
			addNotice( message, 'error' );
			setIsConnecting( false );
		}
	};

	const handleDisconnect = async () => {
		if (
			! window.confirm(
				__(
					'Disconnect Google Calendar? Existing synced events will not be removed from Google Calendar.',
					'nemtly-booking'
				)
			)
		) {
			return;
		}
		setIsDisconnecting( true );
		try {
			await apiFetch( {
				path: '/nemtly-booking/v1/google-auth/disconnect',
				method: 'POST',
			} );
			addNotice(
				__( 'Google Calendar disconnected.', 'nemtly-booking' ),
				'success'
			);
			await loadStatus();
		} catch ( error ) {
			addNotice(
				__( 'Failed to disconnect Google Calendar.', 'nemtly-booking' ),
				'error'
			);
		} finally {
			setIsDisconnecting( false );
		}
	};

	const handleSaveCalendarId = async () => {
		setIsSavingCalId( true );
		try {
			await apiFetch( {
				path: '/nemtly-booking/v1/settings',
				method: 'POST',
				data: { google_calendar_id: calendarId },
			} );
			addNotice(
				__( 'Calendar ID saved.', 'nemtly-booking' ),
				'success'
			);
		} catch ( error ) {
			addNotice(
				__( 'Failed to save Calendar ID.', 'nemtly-booking' ),
				'error'
			);
		} finally {
			setIsSavingCalId( false );
		}
	};

	if ( isLoading ) {
		return (
			<div className="nb-flex nb-items-center nb-gap-3 nb-py-8">
				<Spinner />
				<span className="nb-text-slate-500 nb-text-sm">
					{ __(
						'Loading Google Calendar status…',
						'nemtly-booking'
					) }
				</span>
			</div>
		);
	}

	return (
		<div className="nb-space-y-6">
			{ /* Header card */ }
			<div className="nb-bg-white nb-border nb-border-slate-200 nb-rounded-lg nb-p-6">
				<div className="nb-flex nb-items-start nb-gap-4">
					<div className="nb-flex-shrink-0 nb-w-10 nb-h-10 nb-bg-blue-50 nb-rounded-lg nb-flex nb-items-center nb-justify-center">
						<svg
							className="nb-w-5 nb-h-5 nb-text-blue-600"
							fill="currentColor"
							viewBox="0 0 24 24"
						>
							<path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z" />
						</svg>
					</div>
					<div className="nb-flex-1">
						<h3 className="nb-text-base nb-font-semibold nb-text-slate-800 nb-mb-1">
							{ __( 'Google Calendar', 'nemtly-booking' ) }
						</h3>
						<p className="nb-text-sm nb-text-slate-500">
							{ __(
								'Automatically add confirmed bookings to your Google Calendar and remove them when cancelled.',
								'nemtly-booking'
							) }
						</p>
					</div>
					<div className="nb-flex-shrink-0">
						{ status?.connected ? (
							<span className="nb-inline-flex nb-items-center nb-gap-1.5 nb-px-2.5 nb-py-1 nb-rounded-full nb-text-xs nb-font-medium nb-bg-green-100 nb-text-green-700">
								<span className="nb-w-1.5 nb-h-1.5 nb-rounded-full nb-bg-green-500"></span>
								{ __( 'Connected', 'nemtly-booking' ) }
							</span>
						) : (
							<span className="nb-inline-flex nb-items-center nb-gap-1.5 nb-px-2.5 nb-py-1 nb-rounded-full nb-text-xs nb-font-medium nb-bg-slate-100 nb-text-slate-600">
								<span className="nb-w-1.5 nb-h-1.5 nb-rounded-full nb-bg-slate-400"></span>
								{ __( 'Not connected', 'nemtly-booking' ) }
							</span>
						) }
					</div>
				</div>
			</div>

			{ /* Step 1 – OAuth credentials */ }
			<div className="nb-bg-white nb-border nb-border-slate-200 nb-rounded-lg nb-p-6">
				<h4 className="nb-text-sm nb-font-semibold nb-text-slate-700 nb-mb-1">
					{ __(
						'Step 1: Enter your Google Cloud credentials',
						'nemtly-booking'
					) }
				</h4>
				<p className="nb-text-xs nb-text-slate-500 nb-mb-4">
					{ __(
						'You need a Google Cloud project with the Calendar API enabled and an OAuth 2.0 client. Follow the steps below to get your credentials.',
						'nemtly-booking'
					) }
				</p>

				{ /* How-to guide */ }
				<div className="nb-mb-5 nb-p-4 nb-bg-slate-50 nb-border nb-border-slate-200 nb-rounded-lg">
					<p className="nb-text-xs nb-font-semibold nb-text-slate-700 nb-mb-3">
						{ __(
							'How to get your Client ID and Client Secret:',
							'nemtly-booking'
						) }
					</p>
					<ol className="nb-list-decimal nb-list-inside nb-space-y-2 nb-text-xs nb-text-slate-600">
						<li>
							{ __( 'Go to the', 'nemtly-booking' ) }{ ' ' }
							<a
								href="https://console.cloud.google.com/projectcreate"
								target="_blank"
								rel="noopener noreferrer"
								className="nb-text-blue-600 hover:nb-text-blue-800 nb-underline"
							>
								{ __(
									'Google Cloud Console',
									'nemtly-booking'
								) }
							</a>{ ' ' }
							{ __(
								'and create a new project (or select an existing one).',
								'nemtly-booking'
							) }
						</li>
						<li>
							{ __( 'Enable the', 'nemtly-booking' ) }{ ' ' }
							<a
								href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com"
								target="_blank"
								rel="noopener noreferrer"
								className="nb-text-blue-600 hover:nb-text-blue-800 nb-underline"
							>
								{ __(
									'Google Calendar API',
									'nemtly-booking'
								) }
							</a>{ ' ' }
							{ __( 'for your project.', 'nemtly-booking' ) }
						</li>
						<li>
							{ __( 'Go to', 'nemtly-booking' ) }{ ' ' }
							<a
								href="https://console.cloud.google.com/apis/credentials"
								target="_blank"
								rel="noopener noreferrer"
								className="nb-text-blue-600 hover:nb-text-blue-800 nb-underline"
							>
								{ __(
									'APIs & Services → Credentials',
									'nemtly-booking'
								) }
							</a>
							{ __(
								', click "Create Credentials" → "OAuth client ID".',
								'nemtly-booking'
							) }
						</li>
						<li>
							{ __(
								'If prompted, configure the OAuth consent screen first. Choose "External" user type, fill in the required fields, and add the scope',
								'nemtly-booking'
							) }{ ' ' }
							<code className="nb-text-xs nb-bg-slate-100 nb-px-1 nb-py-0.5 nb-rounded nb-font-mono">
								https://www.googleapis.com/auth/calendar
							</code>
							.
						</li>
						<li>
							{ __(
								'Set Application type to "Web application". Under "Authorized redirect URIs", add the redirect URI shown below.',
								'nemtly-booking'
							) }
						</li>
						<li>
							{ __(
								'Copy the Client ID and Client Secret from the created credential and paste them into the fields below.',
								'nemtly-booking'
							) }
						</li>
					</ol>
					<p className="nb-mt-3 nb-text-xs nb-text-slate-500">
						{ __( 'Need more help?', 'nemtly-booking' ) }{ ' ' }
						<a
							href="https://developers.google.com/calendar/api/quickstart/php"
							target="_blank"
							rel="noopener noreferrer"
							className="nb-text-blue-600 hover:nb-text-blue-800 nb-underline"
						>
							{ __(
								'Google Calendar API Quickstart',
								'nemtly-booking'
							) }
						</a>
						{ ' · ' }
						<a
							href="https://support.google.com/cloud/answer/6158849"
							target="_blank"
							rel="noopener noreferrer"
							className="nb-text-blue-600 hover:nb-text-blue-800 nb-underline"
						>
							{ __( 'Setting up OAuth 2.0', 'nemtly-booking' ) }
						</a>
					</p>
				</div>

				{ /* Redirect URI */ }
				{ status?.redirect_uri && (
					<div className="nb-mb-4">
						<label className="nb-block nb-text-xs nb-font-medium nb-text-slate-700 nb-mb-1">
							{ __(
								'Authorized Redirect URI',
								'nemtly-booking'
							) }
						</label>
						<p className="nb-text-xs nb-text-slate-500 nb-mb-2">
							{ __(
								'Copy this URL and add it as an "Authorized redirect URI" in your Google Cloud OAuth client settings.',
								'nemtly-booking'
							) }
						</p>
						<div className="nb-p-3 nb-bg-slate-50 nb-border nb-border-slate-200 nb-rounded nb-flex nb-items-center nb-justify-between nb-gap-3">
							<span className="nb-text-xs nb-font-mono nb-text-slate-600 nb-break-all">
								{ status.redirect_uri }
							</span>
							<button
								type="button"
								className="nb-flex-shrink-0 nb-text-xs nb-text-slate-500 hover:nb-text-slate-700 nb-underline"
								onClick={ () => {
									navigator.clipboard.writeText(
										status.redirect_uri
									);
									addNotice(
										__(
											'Redirect URI copied to clipboard.',
											'nemtly-booking'
										),
										'success'
									);
								} }
							>
								{ __( 'Copy', 'nemtly-booking' ) }
							</button>
						</div>
					</div>
				) }

				{ /* Credential inputs */ }
				<div className="nb-space-y-4">
					<TextControl
						label={ __( 'Client ID', 'nemtly-booking' ) }
						value={ settings.google_client_id || '' }
						onChange={ ( value ) =>
							updateSetting( 'google_client_id', value )
						}
						placeholder="123456789-abc.apps.googleusercontent.com"
						help={ __(
							'Found on the OAuth client page in Google Cloud Console under "Client ID".',
							'nemtly-booking'
						) }
						className="nb-w-full"
					/>
					<TextControl
						label={ __( 'Client Secret', 'nemtly-booking' ) }
						value={ settings.google_client_secret || '' }
						onChange={ ( value ) =>
							updateSetting( 'google_client_secret', value )
						}
						type="password"
						placeholder={ __(
							'Your Google client secret',
							'nemtly-booking'
						) }
						help={ __(
							'Found on the same page under "Client secret". Keep this value private.',
							'nemtly-booking'
						) }
						className="nb-w-full"
					/>
				</div>

				<p className="nb-mt-3 nb-text-xs nb-text-slate-400">
					{ __(
						'Save Settings to persist your credentials before connecting.',
						'nemtly-booking'
					) }
				</p>
			</div>

			{ /* Step 2 – Connect / Disconnect */ }
			<div className="nb-bg-white nb-border nb-border-slate-200 nb-rounded-lg nb-p-6">
				<h4 className="nb-text-sm nb-font-semibold nb-text-slate-700 nb-mb-1">
					{ __(
						'Step 2: Connect your Google account',
						'nemtly-booking'
					) }
				</h4>
				<p className="nb-text-xs nb-text-slate-500 nb-mb-4">
					{ __(
						'Click the button below to authorise Nemtly to access your Google Calendar.',
						'nemtly-booking'
					) }
				</p>

				{ ! status?.connected ? (
					<Button
						variant="primary"
						onClick={ handleConnect }
						isBusy={ isConnecting }
						disabled={ isConnecting || ! status?.has_creds }
						className="nemtly-button-primary"
					>
						{ isConnecting
							? __( 'Redirecting to Google…', 'nemtly-booking' )
							: __(
									'Connect Google Calendar',
									'nemtly-booking'
							  ) }
					</Button>
				) : (
					<Button
						variant="secondary"
						onClick={ handleDisconnect }
						isBusy={ isDisconnecting }
						disabled={ isDisconnecting }
						className="nemtly-button-danger"
					>
						{ isDisconnecting
							? __( 'Disconnecting…', 'nemtly-booking' )
							: __(
									'Disconnect Google Calendar',
									'nemtly-booking'
							  ) }
					</Button>
				) }

				{ ! status?.has_creds && (
					<p className="nb-mt-2 nb-text-xs nb-text-amber-600">
						{ __(
							'Save your Client ID and Client Secret first.',
							'nemtly-booking'
						) }
					</p>
				) }
			</div>

			{ /* Step 3 – Calendar ID (only shown when connected) */ }
			{ status?.connected && (
				<div className="nb-bg-white nb-border nb-border-slate-200 nb-rounded-lg nb-p-6">
					<h4 className="nb-text-sm nb-font-semibold nb-text-slate-700 nb-mb-1">
						{ __( 'Step 3: Choose a calendar', 'nemtly-booking' ) }
					</h4>
					<p className="nb-text-xs nb-text-slate-500 nb-mb-3">
						{ __(
							'Enter the Calendar ID to sync bookings to. Use "primary" for your main calendar, or paste a specific calendar ID.',
							'nemtly-booking'
						) }
					</p>

					<div className="nb-mb-4 nb-p-3 nb-bg-slate-50 nb-border nb-border-slate-200 nb-rounded-lg">
						<p className="nb-text-xs nb-font-semibold nb-text-slate-700 nb-mb-2">
							{ __(
								'How to find your Calendar ID:',
								'nemtly-booking'
							) }
						</p>
						<ol className="nb-list-decimal nb-list-inside nb-space-y-1 nb-text-xs nb-text-slate-600">
							<li>
								{ __( 'Open', 'nemtly-booking' ) }{ ' ' }
								<a
									href="https://calendar.google.com"
									target="_blank"
									rel="noopener noreferrer"
									className="nb-text-blue-600 hover:nb-text-blue-800 nb-underline"
								>
									{ __(
										'Google Calendar',
										'nemtly-booking'
									) }
								</a>
								{ __( 'in your browser.', 'nemtly-booking' ) }
							</li>
							<li>
								{ __(
									'In the left sidebar, hover over the calendar you want and click the three-dot menu → "Settings and sharing".',
									'nemtly-booking'
								) }
							</li>
							<li>
								{ __(
									'Scroll down to "Integrate calendar" — the Calendar ID is shown there (e.g. "abc123@group.calendar.google.com").',
									'nemtly-booking'
								) }
							</li>
						</ol>
					</div>

					<div className="nb-flex nb-items-end nb-gap-3">
						<div className="nb-flex-1">
							<TextControl
								label={ __( 'Calendar ID', 'nemtly-booking' ) }
								value={ calendarId }
								onChange={ setCalendarId }
								placeholder="primary"
							/>
						</div>
						<Button
							variant="secondary"
							onClick={ handleSaveCalendarId }
							isBusy={ isSavingCalId }
							disabled={ isSavingCalId }
							className="nemtly-button-secondary"
							style={ { marginBottom: '8px' } }
						>
							{ __( 'Save', 'nemtly-booking' ) }
						</Button>
					</div>
				</div>
			) }
		</div>
	);
}
